import { useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import useStore from './store/useStore';
import { useBranding } from './hooks/useBranding';
import { useRouterSync } from './hooks/useRouterSync';
import { ThemeProvider } from './context/ThemeContext';
import LandingPage from './features/landing/pages/LandingPage';
import ResponsiveWarning from './layout/ResponsiveWarning';
import './App.css';

// Lazy load heavy components for code splitting
const ClosetTypePage = lazy(() => import('./features/closet-setup/pages/ClosetTypePage'));
const RoomSetupPage = lazy(() => import('./features/closet-setup/pages/RoomSetupPage'));
const EditorPage = lazy(() => import('./features/design/pages/EditorPage'));
const ReviewPage = lazy(() => import('./features/review/pages/ReviewPage'));
const SavedDesignsPage = lazy(() => import('./features/projects/pages/SavedDesignsPage'));
const BookingPage = lazy(() => import('./features/booking/pages/BookingPage'));
const AdminPage = lazy(() => import('./features/admin/pages/AdminPage'));
const SuperAdminPage = lazy(() => import('./features/super-admin/pages/SuperAdminPage'));
const VerifyEmailPage = lazy(() => import('./features/auth/pages/VerifyEmailPage'));
const NotificationsPage = lazy(() => import('./features/notifications/pages/NotificationsPage'));
const Viewer3DPage = lazy(() => import('./features/view-3d/pages/Viewer3DPage'));
const PrintPreviewPage = lazy(() => import('./features/review/pages/PrintPreviewPage'));
const MobileGeneratePage = lazy(() => import('./features/generate-design/pages/MobileGeneratePage'));
const MobileChoicePage = lazy(() => import('./features/generate-design/pages/MobileChoicePage'));
const MyAccountPage = lazy(() => import('./features/account/pages/MyAccountPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-slate-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Minimum width required for the editor canvas
const EDITOR_MIN_WIDTH = 900;

// Protected route wrapper - redirects to landing if project is required but missing
const RequireProject = ({ children }) => {
  const project = useStore((state) => state.project);
  
  if (!project) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Auth-required route wrapper - redirects anonymous users to landing
// and opens the sign-in modal. Used to gate the design flow (P0).
//
// Hard-reload tolerance: when a user refreshes a protected route, the
// store is still hydrating and `currentUser` is briefly null while
// `initializeApp` re-validates the persisted token. If a token exists
// in localStorage we treat the user as authenticated for routing
// purposes — the store will reconcile within a tick. Anonymous users
// (no token) still get redirected immediately.
const RequireAuth = ({ children }) => {
  const currentUser = useStore((state) => state.currentUser);
  const setAuthModal = useStore((state) => state.setAuthModal);

  const hasToken =
    typeof window !== 'undefined' && !!localStorage.getItem('eoc-auth-token');

  useEffect(() => {
    if (!currentUser && !hasToken) {
      setAuthModal('signin');
    }
  }, [currentUser, hasToken, setAuthModal]);

  if (!currentUser && !hasToken) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App content with router sync
function AppContent() {
  // Use individual selectors so AppContent only re-renders when these specific
  // pieces change (not on every store mutation). Action refs are stable.
  const currentPage = useStore((s) => s.currentPage);
  const showResponsiveWarning = useStore((s) => s.showResponsiveWarning);
  const setShowResponsiveWarning = useStore((s) => s.setShowResponsiveWarning);
  const undo = useStore((s) => s.undo);
  const redo = useStore((s) => s.redo);
  const selectedItemId = useStore((s) => s.selectedItemId);
  const deleteItem = useStore((s) => s.deleteItem);

  // Sync router with Zustand state
  useRouterSync();

  // Check viewport width - only show warning on editor page
  useEffect(() => {
    const checkWidth = () => {
      const isEditorPage = currentPage === 'editor';
      const isTooNarrow = window.innerWidth < EDITOR_MIN_WIDTH;
      setShowResponsiveWarning(isEditorPage && isTooNarrow);
    };
    
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [setShowResponsiveWarning, currentPage]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
      e.preventDefault();
      redo();
    }
    if (e.key === 'Delete' && selectedItemId) {
      e.preventDefault();
      deleteItem(selectedItemId);
    }
  }, [undo, redo, selectedItemId, deleteItem]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      <Routes>
        {/* Special routes with dedicated components */}
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/print-preview" element={<PrintPreviewPage />} />
        
        {/* Main app routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/closet-type" element={
          <RequireAuth>
            <ClosetTypePage />
          </RequireAuth>
        } />
        <Route path="/room-setup" element={
          <RequireAuth>
            <RoomSetupPage />
          </RequireAuth>
        } />
        <Route path="/saved-designs" element={
          <RequireAuth>
            <SavedDesignsPage />
          </RequireAuth>
        } />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/super-admin" element={<SuperAdminPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/mobile-choice" element={
          <RequireAuth>
            <MobileChoicePage />
          </RequireAuth>
        } />
        <Route path="/my-account" element={<MyAccountPage />} />
        
        {/* Project-required routes (also require auth to design) */}
        <Route path="/editor" element={
          <RequireAuth>
            <RequireProject>
              <EditorPage />
            </RequireProject>
          </RequireAuth>
        } />
        <Route path="/review" element={
          <RequireAuth>
            <RequireProject>
              <ReviewPage />
            </RequireProject>
          </RequireAuth>
        } />
        <Route path="/viewer-3d" element={
          <RequireAuth>
            <RequireProject>
              <Viewer3DPage />
            </RequireProject>
          </RequireAuth>
        } />
        <Route path="/mobile-generate" element={
          <RequireAuth>
            <RequireProject>
              <MobileGeneratePage />
            </RequireProject>
          </RequireAuth>
        } />
        
        {/* Fallback - redirect unknown routes to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {showResponsiveWarning && <ResponsiveWarning />}
    </>
  );
}

function App() {
  const initializeApp = useStore((s) => s.initializeApp);

  // Fetch branding at root level
  useBranding();

  useEffect(() => {
    initializeApp();
  }, [initializeApp]);

  return (
    <ThemeProvider>
      <div className="min-h-screen theme-bg-primary">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <AppContent />
          </Suspense>
        </BrowserRouter>
        
        <Toaster 
          position="top-right"
          theme="dark"
          duration={1500}
          toastOptions={{
            style: {
              background: 'var(--theme-bg-tertiary)',
              border: '1px solid var(--theme-border)',
              color: 'var(--theme-text-primary)',
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
